import React, { useState } from "react";
import { FaRegCalendarAlt, FaLock } from "react-icons/fa";
import { messaggioDelServer } from "../utils/enrolDati";

/**
 * Ciò che la plancia clinic e la pagina di un evento hanno IN COMUNE.
 * Cantiere CLINICS / OFFSEASON, Fetta 3 «plancia a pagine» (23/9/2026).
 *
 * Fino alla Fetta 3 la plancia era una pagina sola: l'elenco degli eventi e,
 * a tendina sotto la riga, il dettaglio dell'evento aperto. Da qui due pagine —
 * ClinicEvents.jsx (/clinic-events: «New event» e l'elenco) e
 * ClinicEventDetail.jsx (/clinic-events/:id: tutto ciò che stava nella
 * tendina) — e ciò che servirebbe a entrambe sta QUI, spostato e non copiato:
 * l'intestazione della sezione, l'esecutore dei gesti e la frase d'errore;
 * dalla P1 (24/9) anche le pastiglie dell'evento.
 * Niente di questo modulo è nuovo nel comportamento.
 */

// Che cosa dire quando un gesto non riesce.
//
// ⚠️ SI DISTINGUE IL RIFIUTO DAL GUASTO, come in SeasonManagement e in
// enrolShared: se il server ha risposto, `err.status` c'è (lo attacca api.js) e
// il motivo sta nel corpo — ed è lì che vivono i «no» parlanti di queste rotte
// («A group with that name already exists», «Event date does not exist: …»). Se
// risposta non ce n'è stata, non si può dare la colpa a ciò che l'utente ha
// scritto: può essere caduta la linea, e in quel caso non è stato salvato nulla.
function spiegazioneDiUnErrore(err) {
  const dalServer = messaggioDelServer(err);

  if (dalServer) return dalServer;

  return err && err.status
    ? "The club server refused the change. Nothing was saved."
    : "The club server could not be reached. Nothing was saved.";
}

// IL GESTO, uno solo per tutte le scritture di una pagina.
//
// Tutte le rotte hanno la stessa forma — gate ADMIN/SUPERADMIN, rifiuto in
// `{ error }` — quindi hanno un solo esecutore: cambia che cosa si fa della
// risposta, non il modo in cui si chiede. `nome` è la chiave del pulsante in
// volo; `chiamata` è la promessa da attendere.
//
// QUALE GESTO È IN VOLO — il nome, non un booleano, perché il pulsante che
// l'ha avviato deve poterlo dire ("Saving…") mentre gli altri si limitano a
// spegnersi. Uno per volta: due scritture sovrapposte sullo stesso evento
// lascerebbero a schermo il risultato della più LENTA, non dell'ultima data.
//
// Ogni pagina ha il SUO esecutore (lo stato vive nella pagina che chiama):
// uscendo da una pagina non resta in volo nulla che valga per l'altra.
export function useGestoClinic() {
  const [azioneInCorso, setAzioneInCorso] = useState(null);
  const [erroreAzione, setErroreAzione] = useState(null);

  const esegui = async (nome, chiamata, dopo) => {
    if (azioneInCorso) return false;

    setAzioneInCorso(nome);
    setErroreAzione(null);

    try {
      const risposta = await chiamata();
      if (typeof dopo === "function") dopo(risposta);
      return true;
    } catch (err) {
      setErroreAzione(spiegazioneDiUnErrore(err));
      return false;
    } finally {
      setAzioneInCorso(null);
    }
  };

  return { azioneInCorso, erroreAzione, setErroreAzione, esegui };
}

// L'intestazione della sezione, la stessa sulle due pagine: il titolo con
// l'icona e l'etichetta «Admin».
export function IntestazionePlancia() {
  return (
    <header className="qpsc-clinic__head">
      <div className="qpsc-clinic__title">
        <FaRegCalendarAlt className="qpsc-clinic__title-icon" aria-hidden="true" />
        <h1 className="qpsc-page-title">Clinic / Off Season / Specials</h1>
      </div>
      <div className="qpsc-clinic__role">
        <FaLock aria-hidden="true" />
        <span>Admin</span>
      </div>
    </header>
  );
}

// Le pastiglie di un evento, le stesse nella riga dell'elenco e nella testa
// della pagina dell'evento (P1, 24/9: prima erano disegnate in due posti).
// Il contrassegno HIDDEN sta ACCANTO alla pastiglia dello stato, non al suo
// posto: nascosto e chiuso sono due cose distinte. Lo stato: verde aperto,
// grigia chiusa. Un frammento, così il markup resta quello di prima.
export function PastiglieEvento({ evento }) {
  const isOpen = evento.status === "OPEN";
  const isHidden = evento.hidden === true;

  return (
    <>
      {isHidden && (
        <span className="qpsc-clinic__badge qpsc-clinic__badge--hidden">
          HIDDEN
        </span>
      )}

      <span
        className={
          isOpen
            ? "qpsc-clinic__badge qpsc-clinic__badge--open"
            : "qpsc-clinic__badge qpsc-clinic__badge--closed"
        }
      >
        {isOpen ? "OPEN" : "CLOSED"}
      </span>
    </>
  );
}
